import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
} from '@mui/material';
import { Save as SaveIcon, Close as CloseIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { endpoints } from '../../services/api';
import PermissionTree from './PermissionTree';

/**
 * PermissionDialog - Modal para asignar permisos a un usuario
 * 
 * Props:
 * - open: boolean - si el diálogo está abierto
 * - onClose: función - callback al cerrar
 * - usuario: objeto - usuario al que se le asignarán permisos
 */
const PermissionDialog = ({ open, onClose, usuario }) => {
  const queryClient = useQueryClient();
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Reset permisos seleccionados cuando se cierra el diálogo
  useEffect(() => {
    if (!open) {
      setSelectedPermissions([]);
    }
  }, [open]);

  // Obtener todos los permisos disponibles
  const { 
    data: allPermissions, 
    isLoading: loadingPermissions, 
    error: permissionsError,
    refetch: refetchPermissions 
  } = useQuery({
    queryKey: ['permisos', 'all'],
    queryFn: async () => {
      const response = await api.get(endpoints.permisos.list);
      return response.data.data;
    },
    enabled: open,
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
  });

  // Obtener permisos actuales del usuario
  const { 
    data: userPermissions, 
    isLoading: loadingUserPermissions, 
    error: userPermissionsError,
    refetch: refetchUserPermissions 
  } = useQuery({
    queryKey: ['permisos', 'usuario', usuario?.id],
    queryFn: async () => {
      const response = await api.get(endpoints.permisos.byUser(usuario.id));
      return response.data.data.permisos || [];
    },
    enabled: open && !!usuario?.id,
  });

  // Establecer permisos seleccionados cuando se cargan los del usuario
  useEffect(() => {
    if (userPermissions && Array.isArray(userPermissions)) {
      setSelectedPermissions(userPermissions);
    }
  }, [userPermissions]);

  // Mutación para guardar permisos
  const saveMutation = useMutation({
    mutationFn: async (permisos) => {
      const response = await api.post(endpoints.permisos.assign(usuario.id), {
        permisos,
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidar cache de permisos del usuario
      queryClient.invalidateQueries(['permisos', 'usuario', usuario?.id]);
      
      setSnackbar({
        open: true,
        message: data.message || 'Permisos guardados exitosamente',
        severity: 'success',
      });
      setTimeout(() => {
        onClose();
      }, 1000);
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Error al guardar permisos',
        severity: 'error',
      });
    },
  });

  const handleSave = () => {
    saveMutation.mutate(selectedPermissions);
  };

  const handleClose = () => {
    if (!saveMutation.isPending) {
      onClose();
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRefresh = () => {
    refetchPermissions();
    refetchUserPermissions();
  };

  const isLoading = loadingPermissions || loadingUserPermissions;
  const hasError = permissionsError || userPermissionsError;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '70vh' },
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Permisos de {usuario?.nombre}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {usuario?.email} • {usuario?.cargo || usuario?.tipo}
              </Typography>
            </Box>
            <Box>
              <IconButton onClick={handleRefresh} title="Recargar permisos" size="small">
                <RefreshIcon />
              </IconButton>
              <IconButton onClick={handleClose} title="Cerrar" size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {isLoading ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              minHeight="400px"
              gap={2}
            >
              <CircularProgress />
              <Typography color="text.secondary">Cargando permisos...</Typography>
            </Box>
          ) : hasError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error al cargar permisos: {permissionsError?.message || userPermissionsError?.message || 'Error desconocido'}
              <Button size="small" onClick={handleRefresh} sx={{ ml: 2 }}>
                Reintentar
              </Button>
            </Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                Selecciona los módulos y permisos específicos que tendrá este usuario.
                Si desmarcas un módulo padre, se desmarcarán todos sus sub-permisos.
              </Alert>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Permisos seleccionados: <strong>{selectedPermissions.length}</strong>
                </Typography>
              </Box>

              {allPermissions && Object.keys(allPermissions).length > 0 ? (
                <PermissionTree
                  permissions={allPermissions}
                  selectedPermissions={selectedPermissions}
                  onChange={setSelectedPermissions}
                />
              ) : (
                <Alert severity="warning">
                  No hay permisos disponibles en el sistema. 
                  Ejecute la migración de base de datos primero.
                </Alert>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleClose}
            startIcon={<CloseIcon />}
            disabled={saveMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isLoading || saveMutation.isPending || hasError}
          >
            {saveMutation.isPending ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PermissionDialog;


